(() => {
  // -------------------------
  // Helpers
  // -------------------------
  const norm = (s) => (s || "").toString().trim().toLowerCase();

  // ALT supports multiple colors separated by commas:
  // "Rosewood, Sand, Nude"
  function altHasColor(alt, color) {
    const altNorm = norm(alt);
    const colorNorm = norm(color);
    if (!altNorm || !colorNorm) return false;

    const tokens = altNorm
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    return tokens.includes(colorNorm);
  }

  function setHidden(el, hide) {
    if (hide) {
      el.setAttribute("hidden", "");
      el.style.display = "none";
      el.setAttribute("aria-hidden", "true");
    } else {
      el.removeAttribute("hidden");
      el.style.display = "";
      el.removeAttribute("aria-hidden");
    }
  }

  // -------------------------
  // Color detection (desktop + mobile)
  // -------------------------

  // 1) Your desktop swatches: last fieldset, checked radio, label contains .variant-color-name
  function getColorFromYourDesktopSwatches() {
    const checked = document.querySelector(
      ".block-visibility fieldset:last-of-type input[type='radio']:checked"
    );
    if (!checked) return "";

    let label = checked.closest("label");
    if (!label && checked.nextElementSibling && checked.nextElementSibling.tagName === "LABEL") {
      label = checked.nextElementSibling;
    }

    if (label) {
      const nameEl = label.querySelector(".variant-color-name");
      if (nameEl && nameEl.textContent) return norm(nameEl.textContent);
    }

    return norm(checked.value || "");
  }

  // 2) Generic swatch radios: find a fieldset/option group that looks like "Color"
  function getColorFromAnySwatchGroup() {
    // Look for any checked radio in a fieldset whose legend mentions color/colour
    const fieldsets = Array.from(document.querySelectorAll("fieldset"));
    for (const fs of fieldsets) {
      const legend = fs.querySelector("legend");
      const labelText = norm(legend ? legend.textContent : "");
      if (!labelText) continue;

      if (labelText.includes("color") || labelText.includes("colour")) {
        const checked = fs.querySelector("input[type='radio']:checked");
        if (!checked) continue;

        // If label contains .variant-color-name, use it
        let label = checked.closest("label");
        if (!label && checked.nextElementSibling && checked.nextElementSibling.tagName === "LABEL") {
          label = checked.nextElementSibling;
        }
        if (label) {
          const nameEl = label.querySelector(".variant-color-name");
          if (nameEl && nameEl.textContent) return norm(nameEl.textContent);
        }

        return norm(checked.value || "");
      }
    }
    return "";
  }

  // 3) Mobile dropdown/select: find a select that looks like "Color"
  function getColorFromSelect() {
    const selects = Array.from(document.querySelectorAll("select"));
    for (const sel of selects) {
      // Check label/aria/name/id nearby for "color"
      const aria = norm(sel.getAttribute("aria-label"));
      const name = norm(sel.getAttribute("name"));
      const id = sel.id ? norm(sel.id) : "";

      let labelText = "";
      if (sel.id) {
        const lbl = document.querySelector(`label[for="${CSS.escape(sel.id)}"]`);
        if (lbl) labelText = norm(lbl.textContent);
      }

      const containerText = norm(sel.closest(".product-form, variant-picker, .block-visibility")?.textContent || "");

      const looksLikeColor =
        aria.includes("color") ||
        aria.includes("colour") ||
        name.includes("color") ||
        name.includes("colour") ||
        id.includes("color") ||
        id.includes("colour") ||
        labelText.includes("color") ||
        labelText.includes("colour");

      // If not obvious, but the surrounding container has "Color" and this is the active option selector, still consider it
      const maybeColor =
        !looksLikeColor && (containerText.includes("color") || containerText.includes("colour"));

      if (!looksLikeColor && !maybeColor) continue;

      const opt = sel.options[sel.selectedIndex];
      const value = opt ? opt.textContent || opt.value : sel.value;
      if (value) return norm(value);
    }
    return "";
  }

  // 4) Fallback: current variant -> find which option is Color
  // This helps when mobile UI doesn't expose the color name cleanly.
  let cachedProductJSON = null;

  function getCurrentVariantId() {
    const idInput =
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector('input[name="id"][value]');
    const val = idInput ? idInput.value : "";
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : null;
  }

  function loadProductJSONIfPossible() {
    if (cachedProductJSON) return cachedProductJSON;

    // Common patterns: script[type="application/json"] with "variants"
    const scripts = Array.from(document.querySelectorAll('script[type="application/json"]'));
    for (const s of scripts) {
      const txt = (s.textContent || "").trim();
      if (!txt) continue;
      if (!txt.includes('"variants"') && !txt.includes('"options"')) continue;

      try {
        const obj = JSON.parse(txt);
        // Product JSON shape varies; we want {variants:[], options:[]}
        if (obj && Array.isArray(obj.variants) && Array.isArray(obj.options)) {
          cachedProductJSON = obj;
          return cachedProductJSON;
        }
      } catch (e) {}
    }
    return null;
  }

  function getColorFromVariantJSONFallback() {
    const pid = getCurrentVariantId();
    if (!pid) return "";

    const product = loadProductJSONIfPossible();
    if (!product) return "";

    const variant = product.variants.find((v) => v && v.id === pid);
    if (!variant) return "";

    // Determine which option index is Color/Colour
    // product.options is often ["Size","Color"] or similar
    let colorIndex = -1;
    for (let i = 0; i < product.options.length; i++) {
      const optName = norm(product.options[i]);
      if (optName.includes("color") || optName.includes("colour")) {
        colorIndex = i;
        break;
      }
    }
    if (colorIndex === -1) return "";

    // Variants typically have option1/2/3
    const key = `option${colorIndex + 1}`;
    const val = variant[key];
    return val ? norm(val) : "";
  }

  function getSelectedColorName() {
    return (
      getColorFromYourDesktopSwatches() ||
      getColorFromAnySwatchGroup() ||
      getColorFromSelect() ||
      getColorFromVariantJSONFallback() ||
      ""
    );
  }

  // -------------------------
  // Filtering logic
  // -------------------------
  function filterOneGallery(gallery) {
    const color = getSelectedColorName();

    const items = Array.from(gallery.querySelectorAll("[data-product-media-item][data-media-alt]"));
    const thumbs = Array.from(gallery.querySelectorAll("[data-product-media-thumb][data-media-alt]"));

    if (!items.length) return;

    if (!color) {
      items.forEach((el) => setHidden(el, false));
      thumbs.forEach((el) => setHidden(el, false));
      return;
    }

    let anyMatch = false;

    items.forEach((el) => {
      const alt = el.getAttribute("data-media-alt") || "";
      const match = altHasColor(alt, color);
      if (match) anyMatch = true;
      setHidden(el, !match);
    });

    thumbs.forEach((el) => {
      const alt = el.getAttribute("data-media-alt") || "";
      const match = altHasColor(alt, color);
      setHidden(el, !match);
    });

    // Safety fallback: never leave gallery empty
    if (!anyMatch) {
      items.forEach((el) => setHidden(el, false));
      thumbs.forEach((el) => setHidden(el, false));
      return;
    }

    // Ensure main media stays visible by clicking first visible thumb (best compatibility)
    const firstVisibleThumb = thumbs.find((t) => !t.hasAttribute("hidden"));
    if (firstVisibleThumb) {
      try {
        firstVisibleThumb.click();
      } catch (e) {}
    }
  }

  function applyFilter() {
    const galleries = document.querySelectorAll("media-gallery[data-color-filter-gallery]");
    if (!galleries.length) return;
    galleries.forEach(filterOneGallery);
  }

  // Multi-pass to survive theme async updates
  let timer;
  function runAfterTheme(delay = 80) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      applyFilter();
      setTimeout(applyFilter, 220);
      setTimeout(applyFilter, 520);
    }, delay);
  }

  function bind() {
    // Initial
    applyFilter();
    runAfterTheme(120);

    // Re-run on any variant selection changes
    document.addEventListener("change", (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;

      // common variant inputs
      if (
        t.matches("input[type='radio'], select") &&
        (t.closest(".block-visibility") ||
          t.closest("variant-picker") ||
          t.closest("product-form") ||
          t.closest("form[action*='/cart/add']"))
      ) {
        runAfterTheme(50);
      }
    });

    // Theme events (safe if unused)
    document.addEventListener("variant:change", () => runAfterTheme(50));
    document.addEventListener("product:variant-change", () => runAfterTheme(50));
    document.addEventListener("variantChange", () => runAfterTheme(50));

    // Observe DOM changes (themes rebuild media on variant change)
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "childList" && (m.addedNodes.length || m.removedNodes.length)) {
          runAfterTheme(80);
          break;
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bind);
  } else {
    bind();
  }
})();
